import { useRef, useState, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { formatDate } from '../dates.ts';
import type { RecordOpen } from '../tasks/model.ts';
import { OpenedRecord } from './OpenedRecord.tsx';
import {
  KIND_WORDS, useAttachOrderRecordDocument, useCommissionOrders, useCorrectOrderRecord, useCreateOrderRecord,
  useWithdrawOrderRecord, type CommissionOrder, type OrderRecord, type OrderRecordKind,
} from '../api/orderRecords.ts';
import { ReasonFields, useReasonReady } from './VisitDetails.tsx';
import { Refusal } from './PropertiesPanel.tsx';
import { RecordEdit } from './RecordEdit.ts';
import { useRecordDocument } from './RecordDocument.tsx';
import { NO_DOCUMENT_HERE } from '../legal/recordDocument.ts';

/**
 * Each order the client holds that grants a power, with its commission and its oath and designation (154; Arthur,
 * 27 October 2026). The date shown is the ORDER's: recording writes it there, and the order's deadline completes on
 * it. A document is optional ("No document yet") and, uploaded here, is named and filed by the server in Legal >
 * Court Documents. Who may record is the server's word (may_record).
 */

const field = 'mt-1 w-full min-h-[48px] rounded-lg border border-neutral-300 bg-white px-3 text-base text-neutral-900';
const label = 'block text-sm font-semibold text-neutral-700';
const hint = 'mt-1 text-sm text-neutral-600';
const linkButton = 'mr-3 min-h-[44px] text-sm font-semibold text-neutral-700 underline underline-offset-2';
const primary = 'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';
const secondary = 'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold text-neutral-800 hover:bg-neutral-50';

function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}


export const ORDER_DATE: Record<OrderRecordKind, (o: CommissionOrder) => string | null> = {
  commission: (o) => o.commissioned_date,
  oath_designation: (o) => o.oath_filed_date,
};
export const DATE_WORD: Record<OrderRecordKind, string> = { commission: 'Issued', oath_designation: 'Filed' };

export function OrderRecords({ clientId }: { clientId: string }) {
  const q = useCommissionOrders(clientId);
  // 154 (A-#203): from a commission or oath task, or the task its order's date closed: that order, scrolled to and
  // marked, as Reports marks a filing (A-#194)
  const asked = useLocation().state as RecordOpen | null;
  const showOrder = asked?.show_order ?? null;
  if (q.error && !q.data) return <div className="px-4 pb-3"><Refusal error={q.error} /></div>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading the orders…</p>;
  if (q.data.orders.length === 0) {
    return <p className="px-4 pb-3 text-neutral-600">No order on file grants a power, so none owes a commission.</p>;
  }
  return (
    <ul className="divide-y divide-neutral-100 border-t border-neutral-200">
      {q.data.orders.map((o) => (
        <OpenedRecord key={o.id} id={`order-${o.id}`} opened={o.id === showOrder} from={asked?.opened_from} className="px-4 py-3">
          <p className="font-semibold text-neutral-900">
            {o.label ?? 'Order'}{o.signed_date ? `, signed ${formatDate(o.signed_date)}` : ''}
            {o.powers && <span className="font-normal"> · {o.powers}</span>}
          </p>
          {o.carries_commission ? (
            <p className={hint}>This order carries its own commission: there is no separate commission or oath to record.</p>
          ) : (
            (['commission', 'oath_designation'] as const).map((k) => (
              <KindRow key={k} clientId={clientId} order={o} kind={k} mayRecord={q.data!.may_record}
                       record={o.records.find((r) => r.kind === k && !r.withdrawn) ?? null} />
            ))
          )}
        </OpenedRecord>
      ))}
    </ul>
  );
}

function KindRow({ clientId, order, kind, record, mayRecord }: {
  clientId: string; order: CommissionOrder; kind: OrderRecordKind; record: OrderRecord | null; mayRecord: boolean;
}) {
  const [doing, setDoing] = useState<'record' | 'correct' | 'withdraw' | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const attachInput = useRef<HTMLInputElement>(null);
  const date = ORDER_DATE[kind](order);
  // P-20: the document as the Timeline card and the closed task show it ("View document", or words)
  const recordDoc = useRecordDocument(clientId, record, KIND_WORDS[kind].toLowerCase(), NO_DOCUMENT_HERE);
  return (
    <div className="mt-2">
      <p className="text-neutral-900">
        <span className="font-semibold">{KIND_WORDS[kind]}:</span>{' '}
        {record ? (
          <>
            {DATE_WORD[kind]} {date ? formatDate(date) : 'a date not on the order'}
            {' · '}
            {recordDoc.control}
            {record.recorded_by && <span className="text-sm text-neutral-600"> · recorded by {record.recorded_by}</span>}
            {record.edits > 0 && <span className="text-sm text-neutral-600"> · edited</span>}
          </>
        ) : date ? (
          <>{DATE_WORD[kind]} {formatDate(date)} <span className="text-sm text-neutral-600">(on the order; not recorded here)</span></>
        ) : (
          <span className="text-neutral-700">Not recorded</span>
        )}
      </p>
      {recordDoc.preview}
      {said && <p role="status" className="mt-1 text-sm text-neutral-800">{said}</p>}
      {mayRecord && !doing && (
        <div>
          {!record && (
            <button type="button" className={linkButton} onClick={() => { setDoing('record'); setSaid(null); }}>
              Record the {kind === 'commission' ? 'commission' : 'oath and designation'}
            </button>
          )}
          {record && <AttachInput clientId={clientId} recordId={record.id} input={attachInput} onDone={setSaid} />}
          <RecordEdit may={mayRecord && record !== null} className={linkButton} choices={[
            { key: 'attach', label: 'Attach a document', onPick: () => { setSaid(null); attachInput.current?.click(); } },
            { key: 'correct', label: 'Correct', onPick: () => { setDoing('correct'); setSaid(null); } },
            { key: 'withdraw', label: 'Withdraw: recorded in error', onPick: () => { setDoing('withdraw'); setSaid(null); } },
          ]} />
        </div>
      )}
      {doing === 'record' && (
        <RecordForm clientId={clientId} order={order} kind={kind} initialDate={date ?? ''}
                    onDone={(m) => { setDoing(null); setSaid(m); }} />
      )}
      {doing === 'correct' && record && (
        <CorrectForm clientId={clientId} record={record} kind={kind} date={date ?? ''}
                     onDone={(m) => { setDoing(null); setSaid(m); }} />
      )}
      {doing === 'withdraw' && record && (
        <WithdrawForm clientId={clientId} recordId={record.id} onDone={(m) => { setDoing(null); setSaid(m); }} />
      )}
    </div>
  );
}

/** Exported for Triage (P-31, A-#286), which draws this same form for an order's commission or oath row. */
export function RecordForm({ clientId, order, kind, initialDate, onDone }: {
  clientId: string; order: CommissionOrder; kind: OrderRecordKind; initialDate: string; onDone: (m: string | null) => void;
}) {
  const [date, setDate] = useState(initialDate);
  const [file, setFile] = useState<File | null>(null);
  const create = useCreateOrderRecord(clientId);
  const attach = useAttachOrderRecordDocument(clientId);
  const sending = useRef(false);
  const busy = create.isPending || attach.isPending;
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!date || sending.current) return;
            sending.current = true;
            create.mutate({ orderId: order.id, kind, date }, {
              onSuccess: (r) => {
                if (!file) { onDone('Recorded. The order holds the date, and its deadline is complete.'); return; }
                attach.mutate({ recordId: r.id, file }, {
                  onSuccess: (a) => onDone(`Recorded, with the document filed in Legal > Court Documents as "${a.name}".`),
                  onError: () => onDone('Recorded, but the document was not attached: attach it again.'),
                });
              },
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Record the {kind === 'commission' ? 'commission' : 'oath and designation'}</h3>
      <p className={hint}>
        {kind === 'commission'
          ? 'The day the clerk issued the commission. It is written on the order, and the order\'s commission deadline completes on it.'
          : 'The day the oath and designation were filed. It is written on the order, and its deadline completes on it.'}
      </p>
      <div>
        <label className={label} htmlFor={`or-date-${order.id}-${kind}`}>{kind === 'commission' ? 'Date issued' : 'Date filed'}</label>
        <input id={`or-date-${order.id}-${kind}`} type="date" className={field} value={date} max={todayLocal()}
               min={order.signed_date ?? undefined} onChange={(e) => setDate(e.target.value)} />
      </div>
      <div>
        <label className={label} htmlFor={`or-file-${order.id}-${kind}`}>
          The document <span className="font-normal text-neutral-500">— optional; it can be attached later</span>
        </label>
        <input id={`or-file-${order.id}-${kind}`} type="file" className="mt-1 block min-h-[44px] text-base"
               onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <p className={hint}>Named by the client, the order&rsquo;s powers and the date, and filed in Legal &gt; Court Documents.</p>
      </div>
      {create.error && <Refusal error={create.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!date || busy}>{busy ? 'Recording…' : 'Record'}</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

/** The file picker behind Edit's "Attach a document": kept outside the choices, which close when one is picked. */
function AttachInput({ clientId, recordId, input, onDone }: {
  clientId: string; recordId: string; input: RefObject<HTMLInputElement>; onDone: (m: string) => void;
}) {
  const attach = useAttachOrderRecordDocument(clientId);
  return (
    <>
      {attach.isPending && <p role="status" className="mt-1 text-sm text-neutral-800">Attaching…</p>}
      <input ref={input} type="file" className="hidden" aria-label="The document to attach"
             onChange={(e) => {
               const f = e.target.files?.[0];
               e.target.value = '';
               if (f) attach.mutate({ recordId, file: f }, {
                 onSuccess: (a) => onDone(`Attached, and filed in Legal > Court Documents as "${a.name}".`),
               });
             }} />
      {attach.error && <Refusal error={attach.error} />}
    </>
  );
}

function CorrectForm({ clientId, record, kind, date: initial, onDone }: {
  clientId: string; record: OrderRecord; kind: OrderRecordKind; date: string; onDone: (m: string | null) => void;
}) {
  const [date, setDate] = useState(initial);
  const [keepDocument, setKeepDocument] = useState(true);
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const correct = useCorrectOrderRecord(clientId);
  const reasonReady = useReasonReady(reason, reasonText);
  const hasDocument = Boolean(record.upload_id || record.clio_copy_id);
  const changed = date !== initial || (hasDocument && !keepDocument);
  const sending = useRef(false);
  return (
    <form className="mt-2 space-y-3 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!changed || !reasonReady || sending.current) return;
            sending.current = true;
            correct.mutate({
              recordId: record.id, date,
              upload_id: keepDocument ? record.upload_id : null, clio_copy_id: keepDocument ? record.clio_copy_id : null,
              reason, ...(reasonText.trim() ? { reason_text: reasonText.trim() } : {}),
            }, {
              onSuccess: () => onDone('Saved. The record as it was is kept as an earlier version; the order follows the date.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <h3 className="font-semibold text-neutral-900">Correct the {kind === 'commission' ? 'commission' : 'oath and designation'}</h3>
      <div>
        <label className={label} htmlFor={`orc-date-${record.id}`}>{kind === 'commission' ? 'Date issued' : 'Date filed'}</label>
        <input id={`orc-date-${record.id}`} type="date" className={field} value={date} max={todayLocal()}
               onChange={(e) => setDate(e.target.value)} />
      </div>
      {hasDocument && (
        <label className="flex min-h-[44px] cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 size-5 shrink-0" checked={keepDocument} onChange={(e) => setKeepDocument(e.target.checked)} />
          <span className="text-neutral-900">Keep the linked document</span>
        </label>
      )}
      <ReasonFields reason={reason} text={reasonText} setReason={setReason} setText={setReasonText} idPrefix={`orc-${record.id}`} />
      {!changed && <p className={hint}>Nothing has changed yet.</p>}
      {correct.error && <Refusal error={correct.error} />}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={!changed || !reasonReady || correct.isPending}>
          {correct.isPending ? 'Saving…' : 'Save the change'}
        </button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}

function WithdrawForm({ clientId, recordId, onDone }: { clientId: string; recordId: string; onDone: (m: string | null) => void }) {
  const [reason, setReason] = useState('');
  const withdraw = useWithdrawOrderRecord(clientId);
  const sending = useRef(false);
  return (
    <form className="mt-2 space-y-2 rounded-lg border border-neutral-300 p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!reason.trim() || sending.current) return;
            sending.current = true;
            withdraw.mutate({ recordId, reason: reason.trim() }, {
              onSuccess: () => onDone('Withdrawn. The order\'s date is put back as it was before this record, and its deadline follows.'),
              onError: () => { sending.current = false; },
            });
          }}>
      <p className="text-sm text-neutral-700">
        Only for a record made in error. It stays in the history with your reason; the order&rsquo;s date goes back to what
        it was before this record, and the deadline reopens if that leaves no date.
      </p>
      <label className={label} htmlFor={`orw-${recordId}`}>Why is it being withdrawn?</label>
      <textarea id={`orw-${recordId}`} className={`${field} min-h-[72px] py-2`} value={reason} maxLength={2000}
                onChange={(e) => setReason(e.target.value)} />
      {withdraw.error && <Refusal error={withdraw.error} />}
      <div className="flex gap-2">
        <button type="submit" className={primary} disabled={!reason.trim() || withdraw.isPending}>Withdraw it</button>
        <button type="button" className={secondary} onClick={() => onDone(null)}>Cancel</button>
      </div>
    </form>
  );
}
